import type { Manifest, TargetDTO } from '@devrobot/shared';
import { Plus, X } from 'lucide-react';

export type Route = TargetDTO['routes'][number];
export type Port = TargetDTO['ports'][number];
export type Resources = TargetDTO['resources'];

/** Services a target can expose or limit: from the manifest plus anything already configured. */
export function servicesOf(m: Manifest | null | undefined, extra: string[]): string[] {
  const s = new Set<string>(extra.filter(Boolean));
  if (m) {
    s.add(m.container.appService);
    m.container.builds.forEach((b) => s.add(b.service));
    m.expose.forEach((e) => s.add(e.service));
    m.persistence.databases.forEach((d) => s.add(d.service));
    m.persistence.volumes.forEach((v) => s.add(v.service));
  }
  return [...s].sort();
}

export const routeComplete = (r: Route) => Boolean(r.service && r.hostname.trim() && r.containerPort > 0);
export const portComplete = (p: Port) => Boolean(p.service && p.containerPort > 0 && p.hostPort > 0);

function ServiceSelect({ value, services, onChange, label }: { value: string; services: string[]; onChange: (v: string) => void; label: string }) {
  const options = services.includes(value) || !value ? services : [value, ...services];
  return (
    <select className="select select-sm w-full" value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
      {!value ? <option value="">Choose…</option> : null}
      {options.map((s) => (
        <option key={s} value={s}>
          {s}
        </option>
      ))}
    </select>
  );
}

const num = (v: string) => (v === '' ? 0 : Math.max(0, Math.floor(Number(v)) || 0));

/** `defaults`: hostnames generated from the VPS domain; they are marked so users see which ones follow domain changes. */
export function RoutesEditor({ routes, onChange, services, disabled, defaults = [] }: { routes: Route[]; onChange: (r: Route[]) => void; services: string[]; disabled?: boolean; defaults?: string[] }) {
  const update = (i: number, patch: Partial<Route>) => onChange(routes.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend className="sr-only">Public hostnames</legend>
      {routes.length === 0 ? <p className="text-sm text-base-content/60">No public hostname. The application will not be reachable through the managed HTTPS proxy.</p> : null}
      {routes.map((r, i) => (
        <div key={i} className="grid animate-rise grid-cols-2 items-center gap-2 rounded-field border border-hairline p-2 sm:grid-cols-[minmax(0,9rem)_6rem_minmax(0,1fr)_auto] sm:border-0 sm:p-0">
          <ServiceSelect value={r.service} services={services} onChange={(v) => update(i, { service: v })} label={`Service for hostname ${i + 1}`} />
          <input type="number" min={1} max={65535} className="input input-sm w-full" value={r.containerPort || ''} onChange={(e) => update(i, { containerPort: num(e.target.value) })} aria-label={`Container port for hostname ${i + 1}`} placeholder="port" />
          <label className={`input input-sm col-span-2 w-full sm:col-span-1 ${r.hostname.trim() ? '' : 'input-warning'}`}>
            <input
              className="grow font-mono"
              value={r.hostname}
              onChange={(e) => update(i, { hostname: e.target.value })}
              aria-label={`Hostname ${i + 1}`}
              placeholder="app.example.com"
              autoCapitalize="off"
              spellCheck={false}
            />
            {defaults.includes(r.hostname.trim().toLowerCase()) ? (
              <span className="badge badge-primary badge-soft badge-xs" title="Generated from the VPS domain: it moves automatically if the domain changes. Type another hostname to use your own.">
                default
              </span>
            ) : null}
          </label>
          <button type="button" className="btn btn-ghost btn-sm btn-square justify-self-end" onClick={() => onChange(routes.filter((_, j) => j !== i))} aria-label={`Remove hostname ${r.hostname || i + 1}`}>
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange([...routes, { service: services[0] ?? '', containerPort: 80, hostname: '' }])}>
        <Plus className="size-4" aria-hidden="true" /> Add hostname
      </button>
    </fieldset>
  );
}

export function PortsEditor({ ports, onChange, services, disabled }: { ports: Port[]; onChange: (p: Port[]) => void; services: string[]; disabled?: boolean }) {
  const update = (i: number, patch: Partial<Port>) => onChange(ports.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend className="sr-only">Host ports</legend>
      {ports.length === 0 ? <p className="text-sm text-base-content/60">No host ports are published. Direct exposure is opt-in and checked for collisions.</p> : null}
      {ports.map((p, i) => (
        <div key={i} className="grid animate-rise grid-cols-2 items-center gap-2 rounded-field border border-hairline p-2 sm:grid-cols-[minmax(0,9rem)_6rem_6rem_5.5rem_auto] sm:border-0 sm:p-0">
          <ServiceSelect value={p.service} services={services} onChange={(v) => update(i, { service: v })} label={`Service for port ${i + 1}`} />
          <input type="number" min={1} max={65535} className="input input-sm w-full" value={p.containerPort || ''} onChange={(e) => update(i, { containerPort: num(e.target.value) })} aria-label={`Container port ${i + 1}`} placeholder="container" />
          <input type="number" min={1} max={65535} className="input input-sm w-full" value={p.hostPort || ''} onChange={(e) => update(i, { hostPort: num(e.target.value) })} aria-label={`Host port ${i + 1}`} placeholder="host" />
          <select className="select select-sm w-full" value={p.protocol} onChange={(e) => update(i, { protocol: e.target.value as Port['protocol'] })} aria-label={`Protocol ${i + 1}`}>
            <option value="tcp">tcp</option>
            <option value="udp">udp</option>
          </select>
          <button type="button" className="btn btn-ghost btn-sm btn-square justify-self-end" onClick={() => onChange(ports.filter((_, j) => j !== i))} aria-label={`Remove port ${p.hostPort || i + 1}`}>
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
      ))}
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange([...ports, { service: services[0] ?? '', containerPort: 0, hostPort: 0, protocol: 'tcp' }])}>
        <Plus className="size-4" aria-hidden="true" /> Add host port
      </button>
    </fieldset>
  );
}

export function ResourcesEditor({ resources, onChange, services, disabled }: { resources: Resources; onChange: (r: Resources) => void; services: string[]; disabled?: boolean }) {
  const set = (svc: string, patch: { memory?: string; cpus?: number }) => {
    const next = { ...resources, [svc]: { ...resources[svc], ...patch } };
    const entry = next[svc];
    if (!entry.memory) delete entry.memory;
    if (!entry.cpus) delete entry.cpus;
    if (Object.keys(entry).length === 0) delete next[svc];
    onChange(next);
  };
  if (services.length === 0) return <p className="text-sm text-base-content/60">No services known yet.</p>;
  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend className="sr-only">Resource limits</legend>
      <div className="hidden grid-cols-[minmax(0,9rem)_8rem_7rem] gap-2 text-xs tracking-wide text-base-content/50 uppercase sm:grid">
        <span>Service</span>
        <span>Memory limit</span>
        <span>CPU limit</span>
      </div>
      {services.map((svc) => (
        <div key={svc} className="grid grid-cols-2 items-center gap-2 sm:grid-cols-[minmax(0,9rem)_8rem_7rem]">
          <span className="col-span-2 font-mono text-sm sm:col-span-1">{svc}</span>
          <input
            className="input input-sm w-full font-mono"
            value={resources[svc]?.memory ?? ''}
            onChange={(e) => set(svc, { memory: e.target.value.trim() })}
            placeholder="e.g. 512m"
            aria-label={`Memory limit for ${svc}`}
          />
          <input
            type="number"
            min={0}
            step={0.25}
            className="input input-sm w-full"
            value={resources[svc]?.cpus ?? ''}
            onChange={(e) => set(svc, { cpus: e.target.value === '' ? undefined : Number(e.target.value) })}
            placeholder="e.g. 0.5"
            aria-label={`CPU limit for ${svc}`}
          />
        </div>
      ))}
      <p className="text-xs text-base-content/60">Leave empty for no limit. Memory uses Docker notation (512m, 1g).</p>
    </fieldset>
  );
}
